import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-6161">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-6162">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-6163">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-6164">
            <span className="text-2xl" data-id="element-6165">📝</span>
            <div className="text-left" data-id="element-6166">
              <div className="text-sm font-bold text-indigo-900" data-id="element-6167">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-6168">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-6169">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-6170">
            <div className="text-center" data-id="element-6171">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-6172">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-6173">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-6174">→</div>
            <div className="text-center" data-id="element-6175">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-6176">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-6177">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-6178">
          <table className="w-full" data-id="element-6179">
            <thead data-id="element-6180">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-6181">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-6182">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-6183">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-6184">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-6185">

                    <td className="px-4 py-3" data-id="element-6186">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-6187">

                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-6188">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-6189">

                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-6190">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-6191">
            <div className="flex items-start gap-3" data-id="element-6192">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-6193">
                <span className="text-blue-600 text-lg" data-id="element-6194">🔍</span>
              </div>
              <div data-id="element-6195">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-6196">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-6197">
                  De <strong data-id="element-6198">stam</strong> krijg je door <strong data-id="element-6199">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-6200">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-6201">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-6202">
              <div className="flex items-start gap-3" data-id="element-6203">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-6204">
                  <span className="text-emerald-600 text-lg" data-id="element-6205">📋</span>
                </div>
                <div data-id="element-6206">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-6207">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-6208">
                    <p data-id="element-6209">
                      <strong data-id="element-6210">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6211">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-6212">
                      <strong data-id="element-6213">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6214">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-6215">
                      <strong data-id="element-6216">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6217">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-6218">
                      <strong data-id="element-6219">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6220">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-6221">
            <div className="flex items-start gap-3" data-id="element-6222">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-6223">
                <span className="text-amber-600 text-lg" data-id="element-6224">💡</span>
              </div>
              <div data-id="element-6225">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-6226">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-6227">
                  Bij <strong data-id="element-6228">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-6229">jij loopt</em> wordt{' '}
                  <em data-id="element-6230">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}